import { action, computed, observable } from 'mobx';
import {
  SessionSession,
  Cart as CartType,
  AvailabilityInfo,
  BaseAvailabilityStatus
} from '@linktivity/ars-neo';
import { cartApi, bookingApi } from '@link/shop/services';

export class Cart {
  @observable accessor cart: CartType | null = null;
  @observable accessor sessions: SessionSession[] = [];
  @observable accessor visible = false;
  @observable accessor cartId: string | null = null;
  @observable accessor availabilityInfos: AvailabilityInfo[] = [];
  @observable accessor cartStatus: BaseAvailabilityStatus =
    BaseAvailabilityStatus.AvailabilityStatusOk;
  private getCartSeq = 0;

  private async getSessionsInfo(cart?: CartType) {
    this.setCartId(cart?.id ?? '');
    const sessionIds =
      cart?.items
        ?.map(item => item.sessionId)
        .filter((id): id is string => Boolean(id)) ?? [];

    if (sessionIds.length === 0) {
      this.setSessions([]);
      return;
    }

    await this.getSessions(sessionIds);
    await this.checkCartAvailability();
  }

  @action
  setAvailabilityInfos(availabilityInfos: AvailabilityInfo[]) {
    this.availabilityInfos = availabilityInfos;
  }

  @action
  setCartStatus(cartStatus: BaseAvailabilityStatus) {
    this.cartStatus = cartStatus;
  }

  @action
  async checkCartAvailability() {
    if (!this.cartId) {
      return;
    }
    const res = await cartApi.checkCartAvailability({
      body: {
        cartId: this.cartId
      }
    });
    this.setAvailabilityInfos(res?.availabilityInfos ?? []);
    this.setCartStatus(
      res?.status ?? BaseAvailabilityStatus.AvailabilityStatusOk
    );
    return res?.status;
  }

  @action
  async getCart(cartId?: string) {
    const seq = ++this.getCartSeq;
    const res = await cartApi.getCart({
      body: cartId ? { cartId } : {}
    });
    if (seq !== this.getCartSeq) return;
    await this.getSessionsInfo(res?.cart);
    this.setCart(res?.cart ?? null);
  }

  @action
  setCart(cart: CartType | null) {
    this.cart = cart;
  }

  @action
  setSessions(sessions: SessionSession[]) {
    this.sessions = sessions;
  }

  @action
  async getSessions(sessionIds: string[]) {
    const res = await bookingApi.getSessions({
      body: {
        sessionIds
      }
    });
    this.setSessions(res?.sessions ?? []);
  }

  @action
  setCartId(cartId: string) {
    this.cartId = cartId;
  }

  @action
  async addItem(sessionIds: string[]) {
    if (!this.cartId) {
      return;
    }
    const res = await cartApi.addToCart({
      body: {
        cartId: this.cartId,
        sessionIds
      }
    });
    await this.getSessionsInfo(res?.cart);
    this.setCart(res?.cart ?? null);
  }

  @action
  async removeItem(sessionIds: string[]) {
    if (!this.cartId) {
      return;
    }
    const res = await cartApi.removeFromCart({
      body: {
        cartId: this.cartId,
        sessionIds
      }
    });

    await this.getSessionsInfo(res?.cart);
    this.setCart(res?.cart ?? null);
  }

  @action
  async checkout() {
    if (!this.cartId) {
      return;
    }
    return await cartApi.checkoutCart({
      body: {
        cartId: this.cartId
      }
    });
  }

  @action
  setVisible(visible: boolean) {
    this.visible = visible;
  }

  @action
  open() {
    this.setVisible(true);
  }

  @action
  close() {
    this.setVisible(false);
  }

  @action
  clear() {
    this.setVisible(false);
    this.setCartId('');
    this.setSessions([]);
    this.setCart(null);
  }

  @computed
  get count() {
    return this.sessions.length;
  }

  @computed
  get couponCodes() {
    return this.cart?.couponCodes ?? [];
  }
}

export default new Cart();
